/**
 * Marketing-page view + engagement instrumentation.
 *
 * Two events, both once per route visit / session:
 *   - marketing_page_viewed : every marketing route, so multi-page sessions
 *                             become visible (previously only "/" tracked).
 *   - session_engaged       : the visitor did something a bot almost never
 *                             does — dwelled while visible, scrolled, or
 *                             interacted. A "bounce" is then a human session
 *                             with one page view and no engagement.
 */
import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { trackFunnel } from "@/lib/funnel";

const ENGAGED_KEY = "mr_session_engaged";
const DWELL_MS = 8000;
const SCROLL_RATIO = 0.25;

function alreadyEngaged(): boolean {
  try { return sessionStorage.getItem(ENGAGED_KEY) === "1"; } catch { return false; }
}

function markEngaged(reason: string, path: string) {
  if (alreadyEngaged()) return;
  try { sessionStorage.setItem(ENGAGED_KEY, "1"); } catch { /* ignore */ }
  trackFunnel("session_engaged", { engagement_reason: reason, path }, { once: true });
}

export function useMarketingPageTracking() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Page view: one per route visit.
  useEffect(() => {
    if (typeof window === "undefined") return;
    trackFunnel("marketing_page_viewed", { path: pathname });
  }, [pathname]);

  // Engagement: one per session, whichever signal lands first.
  useEffect(() => {
    if (typeof window === "undefined" || alreadyEngaged()) return;

    let visibleMs = 0;
    let lastTick = Date.now();
    let disposed = false;

    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") visibleMs += Date.now() - lastTick;
      lastTick = Date.now();
      if (visibleMs >= DWELL_MS) {
        markEngaged("dwell", pathname);
        cleanup();
      }
    }, 1000);

    // The scrolling element is not always window: the app shell scrolls an
    // inner container, so listen in the capture phase on document and measure
    // whichever element actually scrolled.
    const onScroll = (e: Event) => {
      let top = window.scrollY;
      let scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const t = e.target;
      if (t && t instanceof HTMLElement) {
        top = t.scrollTop;
        scrollable = t.scrollHeight - t.clientHeight;
      }
      if (scrollable <= 0) return;
      if (top / scrollable >= SCROLL_RATIO) {
        markEngaged("scroll", pathname);
        cleanup();
      }
    };

    const onInteract = () => {
      markEngaged("interaction", pathname);
      cleanup();
    };

    function cleanup() {
      if (disposed) return;
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("touchstart", onInteract);
    }

    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("click", onInteract);
    window.addEventListener("keydown", onInteract);
    window.addEventListener("touchstart", onInteract, { passive: true });


    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
